import i18next, { type TFunction } from 'i18next';
import { vendorLabel } from '@/lib/providers/integration-state';
import type { FlightProviderAttempt } from './types';

// The flight PROVIDER of a rebooking option, and the chain of providers a search asked
// (contract `docs/contracts/airline-air-ticketing.v1.md` §3): rebooking asks the tenant's
// providers in order — the airline provider first, then Duffel, Atlas — and records why each
// did or did not answer, so Operar can say «Ofrecido por Duffel» on an option and, when a PNR
// has no flight or the airline provider was passed over, «Proveedor de la aerolínea: no
// conectado · Duffel: sin ofertas · Atlas: sin ofertas».
//
// Holds keys, never language (the `labels.ts` precedent): the outcome copy lives under
// `rebooking:providerChain` and the provider's name is THE name Proveedores gives it
// (`vendorLabel` over `network:providers.integrations.vendorName`), so the two screens can
// never disagree about who sold a seat. Callers pass their own `t` to redraw on a language
// change; the defaults are bound to the namespace, never to a language.

/** The airline provider's vendor id (contract §0) — the same id Proveedores declares. */
export const AIRLINE_PROVIDER = 'airline-service-provider';

/** Providers whose fare is the AIRLINE's own (its provider, the Sabre GDS) — never a
 *  ticket Nexa buys. The same set as the BFF's `isNexaBought` and rebooking's
 *  `isNexaBoughtProvider`. */
const AIRLINE_FARE_PROVIDERS: ReadonlySet<string> = new Set([AIRLINE_PROVIDER, 'sabre']);

/**
 * Is the option a flight Nexa BUYS (Duffel / Atlas)? Then another airline's leg on it is
 * «Comprar en otras aerolíneas», not interline (policy decision 2026-10-29) — the row must
 * not say «Operado por una aerolínea asociada». `false` without a provider (pre-proxy).
 */
export function isNexaBoughtProvider(provider: string | null | undefined): boolean {
  return !!provider && !AIRLINE_FARE_PROVIDERS.has(provider);
}

/** Live suppliers: their offers are real flights on the real calendar (rebooking's
 *  `isLiveCalendarProvider`). Seeded / airline inventories may be past-dated on purpose. */
const LIVE_CALENDAR_PROVIDERS: ReadonlySet<string> = new Set(['duffel', 'atlas']);

/** A live supplier (`duffel` / `atlas`): its flights are real, on the real calendar. */
export function liveCalendarProvider(provider: string | null | undefined): boolean {
  return !!provider && LIVE_CALENDAR_PROVIDERS.has(provider);
}

/**
 * A live supplier's option whose first leg already left — rebooking refuses to select it
 * (409 `OPTION_DEPARTED`, PO 2026-11-03: SKY M6W2FE, an option of the previous day's search was
 * offered ~23 h after the flight left). The row stays visible, never selectable.
 */
export function optionDeparted(
  option: { provider?: string | null | undefined; segments: ReadonlyArray<{ departure: string }> },
  now: number = Date.now(),
): boolean {
  if (!liveCalendarProvider(option.provider)) return false;
  const at = Date.parse(option.segments[0]?.departure ?? '');
  return Number.isFinite(at) && at <= now;
}

/** Proveedores routes the flight providers under this service (`air-ticketing/search`). */
const FLIGHT_PROVIDER_CATEGORY = 'air-ticketing';

const networkT: TFunction<'network'> = i18next.getFixedT(null, 'network');
const rebookingT: TFunction<'rebooking'> = i18next.getFixedT(null, 'rebooking');

/** A provider's unavailability reasons that have their own words; any other reads «no disponible». */
const REASON_KEY: Readonly<Record<string, string>> = {
  not_connected: 'not_connected',
  // the LCC providers' spellings of «nobody configured it for this tenant»
  not_configured: 'not_connected',
  tenant_not_configured: 'not_connected',
  switched_off: 'switched_off',
  capability_unsupported: 'capability_unsupported',
  upstream_unavailable: 'upstream_unavailable',
  passenger_ages_required: 'passenger_ages_required',
  // an age (operator's or airline's) that does not fit the type: an INF of 2, a CHD of 12
  passenger_age_type_mismatch: 'passenger_age_type_mismatch',
  // Proveedores left it out of the search (integration off, not active, or not participating)
  not_routed: 'not_routed',
  // asked, but its request quota held past the search's wait (Duffel: 30 searches a minute)
  rate_limited: 'rate_limited',
  // a passenger asks for special assistance, which only the provider can confirm
  special_assistance_requires_vendor_confirmation: 'special_assistance',
};

/** THE name of a flight provider — the Proveedores name, never the wire token. */
export function flightProviderName(vendor: string, t: TFunction<'network'> = networkT): string {
  return vendorLabel(t, FLIGHT_PROVIDER_CATEGORY, vendor);
}

/**
 * The airline provider was asked and did not sell (not connected, switched off, down,
 * nothing on the route), was passed over, or Proveedores left it out of the search
 * (`not_routed`) — the chain is worth a line even when another provider offered. `false` when
 * it sold, or when the chain does not mention it at all (one recorded before rebooking named
 * a routed-out airline provider).
 */
export function airlineProviderSkipped(
  attempts: readonly FlightProviderAttempt[] | null | undefined,
): boolean {
  return !!attempts?.some((a) => a.provider === AIRLINE_PROVIDER && a.outcome !== 'offers');
}

/** Verdicts reached without asking any provider: Nexa never searched (no route) or the
 *  policy keeps rebooking off. A chain beside them could only be an earlier run's. */
const NOT_SEARCHED: ReadonlySet<string> = new Set(['NO_ROUTE', 'POLICY_DISABLED']);

/**
 * The chain that explains a PNR's no-options verdict — `undefined` when the verdict was
 * reached without asking a provider, so a stale chain never reads as this run's answer.
 */
export function chainForVerdict(
  attempts: readonly FlightProviderAttempt[] | null | undefined,
  noOptionsReason: string | null | undefined,
): readonly FlightProviderAttempt[] | undefined {
  if (!attempts?.length || (noOptionsReason && NOT_SEARCHED.has(noOptionsReason))) return undefined;
  return attempts;
}

/** The `rebooking:providerChain.outcome.*` key (and count) of one attempt. */
export function attemptOutcomeKey(a: FlightProviderAttempt): { key: string; count?: number } {
  switch (a.outcome) {
    case 'offers':
      return typeof a.offers === 'number' ? { key: 'offers', count: a.offers } : { key: 'offered' };
    case 'empty':
      return { key: 'empty' };
    case 'skipped':
      return { key: 'skipped' };
    case 'error':
      return { key: 'error' };
    default: {
      const reason = a.reason ? REASON_KEY[a.reason] : undefined;
      return { key: reason ? `reason.${reason}` : 'unavailable' };
    }
  }
}

/**
 * The attempts that explain the search: every provider asked, plus the airline provider
 * when it was passed over. A provider skipped because an earlier one already offered says
 * nothing about why — it is left out.
 */
export function explainingAttempts(
  attempts: readonly FlightProviderAttempt[] | null | undefined,
): FlightProviderAttempt[] {
  return (attempts ?? []).filter((a) => a.outcome !== 'skipped' || a.provider === AIRLINE_PROVIDER);
}

/**
 * «Proveedor de la aerolínea: no conectado · Duffel: sin ofertas · Atlas: sin ofertas» —
 * one line, in the order rebooking asked. `undefined` when there is nothing to explain.
 */
export function providerChainLine(
  attempts: readonly FlightProviderAttempt[] | null | undefined,
  t: TFunction = rebookingT,
  tNetwork: TFunction<'network'> = networkT,
): string | undefined {
  const links = explainingAttempts(attempts);
  if (!links.length) return undefined;
  return links
    .map((a) => {
      const { key, count } = attemptOutcomeKey(a);
      return t('rebooking:providerChain.link', {
        provider: flightProviderName(a.provider, tNetwork),
        outcome: t(`rebooking:providerChain.outcome.${key}`, count === undefined ? {} : { count }),
      });
    })
    .join(' · ');
}
